import { computed, ref } from "vue";
import { useOmnisearchStore } from "#ce/apps/shared/store/omnisearch-store";

// The Specialty filter is a single facet over clinical "skills". It is a thin
// selection/search layer over the shared omnisearch store's `skill` item set.
// The store owns the data (one fetch per embed, shared with the omnisearch
// launcher and the provider facet); this composable only selects and searches.

/**
 * Walk a nested quantum-select item tree, indexing every node (parents and
 * children) so a selection can be resolved back to its routing payload and a
 * stored value can be resolved back to its display text.
 *
 * @param {Array<Object>} nodes
 * @returns {{ byNode: Map, byPayload: Map }} `byNode` keyed by the node's unique
 *   render `value`; `byPayload` keyed by the semantic `payload.value` (the skill
 *   slug applied to the search), first occurrence wins.
 */
function indexNodes(nodes) {
  const byNode = new Map();
  const byPayload = new Map();

  const walk = (list) => {
    for (const node of list ?? []) {
      byNode.set(node.value, node);
      const payloadValue = node.payload?.value;
      if (payloadValue != null && !byPayload.has(payloadValue)) {
        byPayload.set(payloadValue, node);
      }
      if (node.children) {
        walk(node.children);
      }
    }
  };

  walk(nodes);
  return { byNode, byPayload };
}

export const useSpecialtyFilter = (options = {}) => {
  const store = options.store ?? useOmnisearchStore(options.uuid);

  // The store splits the skill sections into idle (browsable common specialties,
  // shown when the input is empty) and search (the full searchable skill list,
  // shown while typing). `<sp-quantum-select>` filters the active set by `:search`.
  const idleItems = computed(() => store.skill.value.idleItems);
  const searchItems = computed(() => store.skill.value.searchItems);

  const searchQuery = ref("");
  const hasSearchQuery = computed(() => searchQuery.value.trim().length > 0);
  const models = computed(() => (hasSearchQuery.value ? searchItems.value : idleItems.value));

  // Index the currently displayed set once per change; both the render-value
  // resolution and `payloadValueForNode` read from it rather than re-walking the
  // tree on every call.
  const modelsIndex = computed(() => indexNodes(models.value));

  // The applied skill value (the `payload.value` slug, e.g. `specialty-8462-…`),
  // not the node's render value. This is what the `skill` filter param receives.
  const selectedValue = ref(options.modelValue ?? undefined);

  // Lookup map spanning both display sets, so the activator can show the selected
  // skill's text even when the relevant set isn't currently displayed. Indexing the
  // concatenation keeps `indexNodes`' first-occurrence-wins contract: idle entries
  // win over search for the same payload value.
  const nodesByPayloadValue = computed(
    () => indexNodes([...(idleItems.value ?? []), ...(searchItems.value ?? [])]).byPayload,
  );

  const selectedNode = computed(() =>
    selectedValue.value != null ? nodesByPayloadValue.value.get(selectedValue.value) : undefined,
  );
  const selectedText = computed(() => selectedNode.value?.title);

  // Resolve a node's render `value` (emitted by `@update-selected`) to its
  // semantic skill value within the currently displayed set.
  function payloadValueForNode(value) {
    const node = modelsIndex.value.byNode.get(value);
    return node?.payload?.value ?? node?.value;
  }

  // The render `value` of the selected skill within the currently displayed set,
  // so `<sp-quantum-select :selected>` highlights the right row across the
  // idle/search swap (the same skill carries a different render value per set).
  const selectedRenderValue = computed(() => {
    if (selectedValue.value == null) {
      return undefined;
    }
    return modelsIndex.value.byPayload.get(selectedValue.value)?.value;
  });

  return {
    loading: store.loading,
    error: store.error,
    idleItems,
    searchItems,
    models,
    searchQuery,
    hasSearchQuery,
    selectedValue,
    selectedNode,
    selectedText,
    selectedRenderValue,

    payloadValueForNode,
  };
};
